// disOS shell: hash router (works from static hosting), desktop frame, panes, keyboard and printing.
import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { createHashRouter, Navigate, RouterProvider, useNavigate, useParams } from 'react-router-dom';
import { Landing } from '../pages/Landing';
import { AuthPage } from '../pages/AuthPage';
import { Profile } from '../pages/Profile';
import { RequireAuth } from '../auth/RequireAuth';
import { useAuth } from '../auth/authStore';
import { act, KEYMAP } from './actions';
import { CaseViewContext, useBuildCaseView, useCaseView, type CaseView } from './caseView';
import { Banner, DocTabs, MenuBar, StatusBar, TitleBar, Toolbar, useClientName } from './Frame';
import { ScenarioBar } from './ScenarioBar';
import { Dialogs } from './dialogs/Dialogs';
import { RightPane } from './RightPane';
import { StartPage } from './StartPage';
import { clampFr, clampSummW, flushAll, saveLayout, useActiveDoc, useDis, type Doc, type LeftTab } from './store';
import { SummaryPane } from './SummaryPane';
import { Tour, TOUR_SEEN_KEY } from './Tour';
import { CaseData } from './tabs/CaseData';
import { Children } from './tabs/Children';
import { Sensitivity } from './tabs/Sensitivity';
import { PartiesTab, QbidTab, SupportTab, TaxDataTab, TaxesTab } from './tabs/Small';
import { lazy, Suspense } from 'react';

// Reports (the hybrid-app report components, bench report, analysis reports) load on first use.
const ReportTree = lazy(() => import('./reports/Workspace').then((m) => ({ default: m.ReportTree })));
const Preview = lazy(() => import('./reports/Workspace').then((m) => ({ default: m.Preview })));
const PrintRoot = lazy(() => import('./reports/Workspace').then((m) => ({ default: m.PrintRoot })));
const PreviewMode = lazy(() => import('./preview/PreviewMode').then((m) => ({ default: m.PreviewMode })));
const Wait = () => <div className="ph muted" style={{ padding: 20 }}>Loading…</div>;

function PrintHost({ v }: { v: CaseView }) {
  const printing = useDis((s) => s.ui.print !== null);
  return printing ? <Suspense fallback={null}><PrintRoot v={v} /></Suspense> : null;
}
import { ResultContext } from '../store/useResult';

const LEFT_TABS: [LeftTab, string][] = [['data', 'Case Data'], ['children', 'Children'], ['support', 'Support'], ['taxes', 'Taxes'], ['parties', 'Parties'], ['qbid', 'QBID'], ['taxdata', 'Tax Data'], ['sensitivity', 'Sensitivity'], ['reports', 'Reports']];

function LeftPane() {
  const left = useDis((s) => s.ui.left);
  const setUi = useDis((s) => s.setUi);
  return (
    <>
      <div className="subtabs" role="tablist">
        {LEFT_TABS.map(([id, l], i) => <button key={id} type="button" role="tab" data-tour={`tab-${id}`} className={left === id ? 'on' : ''} aria-selected={left === id} onClick={() => setUi({ left: id })}>{l}<span className="k">{i + 1}</span></button>)}
      </div>
      <div className="pbody" id="leftBody">
        {left === 'data' && <CaseData />}
        {left === 'children' && <Children />}
        {left === 'support' && <SupportTab />}
        {left === 'taxes' && <TaxesTab />}
        {left === 'parties' && <PartiesTab />}
        {left === 'qbid' && <QbidTab />}
        {left === 'taxdata' && <TaxDataTab />}
        {left === 'sensitivity' && <Sensitivity />}
        {left === 'reports' && <Suspense fallback={<Wait />}><ReportTree /></Suspense>}
      </div>
    </>
  );
}

/** A draggable vertical divider; `onDrag` gets the incremental pixel delta as the pointer moves. The grip dots
 *  (see .splitter CSS) signal that it can be dragged. */
function Splitter({ onDrag, title }: { onDrag: (dx: number) => void; title: string }) {
  const last = useRef(0);
  const begin = (e: ReactPointerEvent) => {
    e.preventDefault();
    last.current = e.clientX;
    const move = (ev: PointerEvent) => { onDrag(ev.clientX - last.current); last.current = ev.clientX; };
    const end = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', end); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', end);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };
  return <div className="splitter grip" role="separator" aria-orientation="vertical" title={title} onPointerDown={begin} />;
}

function CaseWork() {
  const v = useCaseView();
  const left = useDis((s) => s.ui.left);
  const preview = useDis((s) => s.ui.preview);
  const setUi = useDis((s) => s.setUi);
  const summaryOpen = useDis((s) => s.ui.summaryOpen);
  const summW = useDis((s) => s.ui.summW);
  const leftFr = useDis((s) => s.ui.leftFr);
  const gridLabelW = useDis((s) => s.ui.gridLabelW);
  const gridFatherW = useDis((s) => s.ui.gridFatherW);
  const workRef = useRef<HTMLDivElement>(null);
  const isReports = left === 'reports';
  const showSummary = summaryOpen && !isReports;

  useEffect(() => { saveLayout({ summaryOpen, summW, leftFr, gridLabelW, gridFatherW }); }, [summaryOpen, summW, leftFr, gridLabelW, gridFatherW]);
  // C29: first-run onboarding — open the tour once (the flag is set when it's closed/finished).
  useEffect(() => { try { if (!localStorage.getItem(TOUR_SEEN_KEY)) setUi({ tourOpen: true }); } catch { /* ignore */ } }, [setUi]);

  // Grid fills its pane edge-to-edge (fr units, no cap). Inputs:Results default 57:43 and are draggable; the
  // Summary is a third portion at a draggable pixel width. Dragging the Summary keeps the Inputs:Results ratio.
  const cols = isReports
    ? undefined
    : showSummary
      ? `minmax(0,${leftFr}fr) 6px minmax(0,${100 - leftFr}fr) 6px ${summW}px`
      : `minmax(0,${leftFr}fr) 6px minmax(0,${100 - leftFr}fr)`;

  // Drag handlers read the LIVE values from the store each move (not the render-time closure), so the
  // incremental deltas accumulate instead of snapping back to the value captured at pointer-down.
  const dragLeft = (dx: number) => {
    const el = workRef.current; if (!el) return;
    const st = useDis.getState().ui;
    const showSum = st.summaryOpen && left !== 'reports';
    const frPx = el.clientWidth - (showSum ? st.summW : 0) - (showSum ? 12 : 6);
    if (frPx <= 0) return;
    setUi({ leftFr: clampFr(((st.leftFr / 100) * frPx + dx) / frPx * 100) });
  };
  const dragSumm = (dx: number) => setUi({ summW: clampSummW(useDis.getState().ui.summW - dx) });

  return (
    <>
      <Banner />
      <div ref={workRef} className={`work${isReports ? ' reports' : ''}`} style={cols ? { gridTemplateColumns: cols } : undefined}>
        <div className="pane left"><LeftPane /></div>
        {isReports
          ? <div className="splitter" aria-hidden="true" />
          : <Splitter onDrag={dragLeft} title="Drag to resize Inputs / Results" />}
        <div className="pane right" data-tour="results">{isReports ? <Suspense fallback={<Wait />}><Preview /></Suspense> : <RightPane />}</div>
        {showSummary && (
          <>
            <Splitter onDrag={dragSumm} title="Drag to resize Summary" />
            <div className="pane summary" data-tour="summary">
              <div className="subtabs">
                <span className="patitle">Summary</span>
                <span className="sp" />
                <button type="button" className="xclose" aria-label="Close Summary pane" title="Close Summary (reopen with “+ Summary” in Results, or press U)" onClick={() => setUi({ summaryOpen: false })}>✕</button>
              </div>
              <div className="pbody"><div className="rbody"><SummaryPane v={v} /></div></div>
            </div>
          </>
        )}
      </div>
      <StatusBar v={v} />
      <PrintHost v={v} />
      {preview && <Suspense fallback={null}><PreviewMode /></Suspense>}
      <Tour />
      <Dialogs inCase />
    </>
  );
}

/** Providers for one open case (keyed by the document so debounced state never leaks between tabs). */
function CaseScreen({ doc }: { doc: Doc }) {
  const v: CaseView = useBuildCaseView(doc);
  return (
    <CaseViewContext.Provider value={v}>
      <ResultContext.Provider value={{ result: v.result, engineError: v.engineError, labels: v.labels }}>
        <CaseWork />
      </ResultContext.Provider>
    </CaseViewContext.Provider>
  );
}

/** Reports tab in the left pane shows the tree; LeftPane above covers the other tabs (tree is also reachable via 8). */
function Shell() {
  const doc = useActiveDoc();
  const clientName = useClientName(doc?.clientId);
  const displayName = clientName || doc?.name || 'Untitled case';
  const toast = useDis((s) => s.ui.toast);
  const menu = useDis((s) => s.ui.menu);
  const setUi = useDis((s) => s.setUi);
  useKeyboard();
  useRouteSync();
  // Signed-in users land on the Start page, where they choose New case / Open / Import.
  useEffect(() => {
    const onHide = () => flushAll();
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onHide);
    return () => { window.removeEventListener('pagehide', onHide); window.removeEventListener('beforeunload', onHide); };
  }, []);
  useEffect(() => { document.title = doc ? `${displayName} · disOS` : 'disOS'; }, [doc, displayName]);
  return (
    <div id="app" onMouseDown={(e) => { if (menu !== null && !(e.target as HTMLElement).closest('.menubar')) setUi({ menu: null }); }}>
      <TitleBar />
      <MenuBar />
      <Toolbar />
      <DocTabs />
      {doc && <ScenarioBar />}
      {doc ? <CaseScreen key={doc.key} doc={doc} /> : (
        <>
          <div className="work start"><StartPage /></div>
          <StatusBar v={null} />
          <Dialogs inCase={false} />
        </>
      )}
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}

/** URL ↔ active tab: #/ is Start, #/case/<id> opens (loading if needed) and activates that case. */
function useRouteSync() {
  const { id } = useParams<{ id?: string }>();
  const nav = useNavigate();
  const active = useDis((s) => s.active);
  const doc = useActiveDoc();
  const openCase = useDis((s) => s.openCase);
  const activate = useDis((s) => s.activate);
  const target = doc ? doc.caseId ?? doc.key : null;
  // URL changed by the user (deep link, back/forward): follow it.
  useEffect(() => {
    if (!id) { if (active !== 'start') activate('start'); return; }
    if (id === target) return;
    const s = useDis.getState();
    const open = s.docs.find((d) => d.caseId === id || d.key === id);
    if (open) activate(open.key);
    else if (id.startsWith('new-')) nav('/app', { replace: true });
    else void openCase(id).then((k) => { if (!k) nav('/app', { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // Active tab changed in the app: reflect it in the URL (under /app).
  useEffect(() => {
    const want = target ? `/app/case/${encodeURIComponent(target)}` : '/app';
    const have = id ? `/app/case/${encodeURIComponent(id)}` : '/app';
    if (want !== have) nav(want);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
}

function useKeyboard() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = useDis.getState();
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      // Preview mode handles its own keys (Esc, arrows, Ctrl+P); Ctrl+S still saves.
      if (s.ui.preview) { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); act('save'); } return; }
      if (e.key === 'Escape') { if (s.ui.menu !== null) s.setUi({ menu: null }); return; }
      if (s.ui.dialog) return;
      if (e.key === 'F9') { e.preventDefault(); act('recalc'); return; }
      if (e.key === 'F1') { e.preventDefault(); act('r:help'); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); act('save'); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p' && s.active !== 'start') { e.preventDefault(); act('print:bench'); return; }
      if (typing || e.ctrlKey || e.metaKey || e.altKey) return;
      if (s.active === 'start') { if (e.key === '?') act('about'); return; }
      if (e.key === 'Delete' && s.ui.sel === 'exempt') { act('restoreAuto'); return; }
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && s.ui.left === 'data') {
        const rows = Array.from(document.querySelectorAll<HTMLTableRowElement>('#grid tbody tr[data-row]'));
        const i = rows.findIndex((r) => r.dataset.row === s.ui.sel);
        const nx = rows[Math.max(0, Math.min(rows.length - 1, i + (e.key === 'ArrowUp' ? -1 : 1)))];
        if (nx?.dataset.row) { e.preventDefault(); s.setUi({ sel: nx.dataset.row, helpTopic: null }); }
        return;
      }
      const cmd = KEYMAP[e.key.toLowerCase()] ?? KEYMAP[e.key];
      if (cmd) { e.preventDefault(); act(cmd); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

const router = createHashRouter([
  { path: '/', element: <Landing /> },
  { path: '/login', element: <AuthPage /> },
  { path: '/app', element: <RequireAuth><Shell /></RequireAuth> },
  { path: '/app/case/:id', element: <RequireAuth><Shell /></RequireAuth> },
  { path: '/app/profile', element: <RequireAuth><Profile /></RequireAuth> },
  { path: '*', element: <Navigate to="/" replace /> },
]);

export function App() {
  const load = useAuth((s) => s.load);
  useEffect(() => { void load(); }, [load]);
  return <RouterProvider router={router} />;
}
